import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Divider,
  Chip,
  CircularProgress,
  Stack,
  useMediaQuery,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { useMutation, gql } from "@apollo/client";

// GraphQL mutation for searching badge
const SEARCH_BADGE = gql`
  mutation SearchBadge(
    $eventId: String!
    $role: String!
    $searchData: String!
  ) {
    searchBadge(eventId: $eventId, role: $role, searchData: $searchData)
  }
`;

// Helper function to determine what text to display based on language features
const getLanguageDisplay = (featuresList) => {
  const hasArabic = featuresList.includes("arabic");
  const hasEnglish = featuresList.includes("english");

  // If arabic-only (has arabic but not english)
  if (hasArabic && !hasEnglish) {
    return { showArabic: true, showEnglish: false };
  }

  // If english-only (has english but not arabic)
  if (hasEnglish && !hasArabic) {
    return { showArabic: false, showEnglish: true };
  }

  // If both are enabled, or neither is specified (default to both)
  return { showArabic: true, showEnglish: true };
};

export default function SearchModal({
  eventId,
  role,
  open,
  onClose,
  featuresList = [],
  customTheme,
}) {
  const [searchId, setSearchId] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [isEmailEnabled, setIsEmailEnabled] = useState(true);
  const [isMobileNumberEnabled, setIsMobileNumberEnabled] = useState(true);

  const { control, handleSubmit, watch } = useForm();
  const emailValue = watch("email");
  const mobileNumberValue = watch("mobileNumber");

  const [searchBadge, { loading, error }] = useMutation(SEARCH_BADGE, {
    onCompleted: (data) => {
      const searchResult = data.searchBadge;
      setSearchId(searchResult);
      setShowButton(!!searchResult);
    },
    onError: (error) => {
      console.error("Failed to search for badge.", error);
      setShowButton(false);
    },
  });

  // Language display logic
  const languageDisplay = getLanguageDisplay(featuresList);

  const handleSearch = async (variables) => {
    const searchData = variables.email || variables.mobileNumber;

    try {
      await searchBadge({
        variables: {
          searchData,
          eventId,
          role,
        },
      });
    } catch (error) {
      console.error("Failed to search for badge.", error);
      setShowButton(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  useEffect(() => {
    setIsEmailEnabled(mobileNumberValue === "");
    setIsMobileNumberEnabled(emailValue === "");
  }, [emailValue, mobileNumberValue]);

  useEffect(() => {
    setIsEmailEnabled(true);
    setIsMobileNumberEnabled(true);
  }, [open]);

  const downloadUrl = `https://api-reg.eventegration.com/graphql/badge/${eventId}/${role}/${searchId}`;

  const handleDownload = () => {
    window.open(downloadUrl);
    setShowButton(null);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      sx={{
        background: "rgba(0, 0, 0, 0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box
        sx={{
          width: isSmallScreen ? "80%" : 400,
          height: "auto",
          position: "relative",
          bgcolor: customTheme?.palette?.background?.paper || "white",
          outline: "none",
          p: 4,
          borderRadius: 7,
          border: `1px solid ${
            customTheme?.palette?.primary?.main || "#1976d2"
          }20`,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 20,
            right: 20,
            color: customTheme?.palette?.primary?.main || "inherit",
            "&:hover": {
              backgroundColor: `${
                customTheme?.palette?.primary?.main || "#1976d2"
              }10`,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        {!showButton ? (
          <>
            {/* Title */}
            {languageDisplay.showArabic && (
              <Typography
                variant="h5"
                textAlign="center"
                gutterBottom
                sx={{
                  direction: "rtl",
                  color: customTheme?.palette?.primary?.main || "inherit",
                }}
              >
                ابحث عن بطاقة الدخول الخاصة بك
              </Typography>
            )}
            {languageDisplay.showEnglish && (
              <Typography
                variant="h5"
                textAlign="center"
                gutterBottom
                sx={{
                  color: customTheme?.palette?.primary?.main || "inherit",
                }}
              >
                Search your Badge
              </Typography>
            )}

            {/* Subtitle */}
            {languageDisplay.showArabic && (
              <Typography
                variant="subtitle1"
                textAlign="center"
                gutterBottom
                sx={{ direction: "rtl" }}
              >
                أدخل البريد الإلكتروني المسجل أو رقم الهاتف المحمول للبحث عن
                بطاقة الدخول الخاصة بك
              </Typography>
            )}
            {languageDisplay.showEnglish && (
              <Typography variant="subtitle1" textAlign="center" gutterBottom>
                Enter your registered email ID or mobile number to search your
                badge.
              </Typography>
            )}

            <form onSubmit={handleSubmit(handleSearch)}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="email"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          languageDisplay.showArabic &&
                          languageDisplay.showEnglish
                            ? "البريد الإلكتروني / Email"
                            : languageDisplay.showArabic
                            ? "البريد الإلكتروني"
                            : "Email"
                        }
                        type="email"
                        fullWidth
                        required
                        disabled={!isEmailEnabled}
                        inputProps={{
                          style: {
                            direction: languageDisplay.showArabic
                              ? "rtl"
                              : "ltr",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor:
                                customTheme?.palette?.primary?.main ||
                                "#1976d2",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor:
                                customTheme?.palette?.primary?.main ||
                                "#1976d2",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color:
                              customTheme?.palette?.primary?.main || "#1976d2",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Divider>
                    <Chip
                      label={
                        languageDisplay.showArabic &&
                        languageDisplay.showEnglish
                          ? "أو / OR"
                          : languageDisplay.showArabic
                          ? "أو"
                          : "OR"
                      }
                      sx={{
                        backgroundColor: `${
                          customTheme?.palette?.primary?.main || "#1976d2"
                        }10`,
                        color: customTheme?.palette?.primary?.main || "inherit",
                      }}
                    />
                  </Divider>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="mobileNumber"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={
                          languageDisplay.showArabic &&
                          languageDisplay.showEnglish
                            ? "رقم الهاتف المحمول / Mobile Number"
                            : languageDisplay.showArabic
                            ? "رقم الهاتف المحمول"
                            : "Mobile Number"
                        }
                        type="number"
                        fullWidth
                        required
                        disabled={!isMobileNumberEnabled}
                        inputProps={{
                          style: {
                            direction: languageDisplay.showArabic
                              ? "rtl"
                              : "ltr",
                          },
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            "&:hover fieldset": {
                              borderColor:
                                customTheme?.palette?.primary?.main ||
                                "#1976d2",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor:
                                customTheme?.palette?.primary?.main ||
                                "#1976d2",
                            },
                          },
                          "& .MuiInputLabel-root.Mui-focused": {
                            color:
                              customTheme?.palette?.primary?.main || "#1976d2",
                          },
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      backgroundColor:
                        customTheme?.palette?.primary?.main || "#1976d2",
                      "&:hover": {
                        backgroundColor:
                          customTheme?.palette?.primary?.dark ||
                          customTheme?.palette?.secondary?.main ||
                          "#1565c0",
                      },
                      "&:disabled": {
                        backgroundColor:
                          customTheme?.palette?.action?.disabled || "#ccc",
                      },
                    }}
                  >
                    {languageDisplay.showArabic && languageDisplay.showEnglish
                      ? "بحث | Search"
                      : languageDisplay.showArabic
                      ? "بحث"
                      : "Search"}
                    {loading && (
                      <CircularProgress
                        color="inherit"
                        size={20}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Button>
                </Grid>
                {error && (
                  <Stack
                    direction="row"
                    justifyContent={"center"}
                    sx={{ width: "100%" }}
                  >
                    <Typography
                      textAlign={"center"}
                      color="error"
                      sx={{ mt: 2 }}
                    >
                      {error.message}
                    </Typography>
                  </Stack>
                )}
              </Grid>
            </form>
          </>
        ) : (
          <>
            <Stack direction={"column"} justifyContent={"center"} spacing={3}>
              {languageDisplay.showArabic && (
                <Typography
                  variant="h5"
                  textAlign="center"
                  gutterBottom
                  sx={{
                    direction: "rtl",
                    color: customTheme?.palette?.primary?.main || "inherit",
                  }}
                >
                  قم بتنزيل بطاقة الدخول الخاصة بك
                </Typography>
              )}
              {languageDisplay.showEnglish && (
                <Typography
                  variant="h5"
                  textAlign="center"
                  gutterBottom
                  sx={{
                    color: customTheme?.palette?.primary?.main || "inherit",
                  }}
                >
                  Download your Badge
                </Typography>
              )}
              <Button
                onClick={handleDownload}
                variant="contained"
                color="success"
                fullWidth
                sx={{
                  backgroundColor:
                    customTheme?.palette?.success?.main || "#2e7d32",
                  "&:hover": {
                    backgroundColor:
                      customTheme?.palette?.success?.dark || "#1b5e20",
                  },
                }}
              >
                {languageDisplay.showArabic && languageDisplay.showEnglish
                  ? "تحميل / Download"
                  : languageDisplay.showArabic
                  ? "تحميل"
                  : "Download"}
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </Modal>
  );
}
